import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { parseVtt } from "@/lib/vtt";

const PREF = "reel:sub-lang";
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

async function fetchVtt(vid, track, alive) {
  for (let i = 0; i < 300 && alive(); i++) {
    const r = await api.get(`/videos/${vid}/subtitles/${encodeURIComponent(track)}`, {
      responseType: "text", validateStatus: (s) => s === 200 || s === 202,
    });
    if (r.status === 200) return r.data;
    await sleep(3000);
  }
  return null;
}

export function useTracks(video, pb) {
  const { base, load, extra, now } = pb;
  const plex = video.source === "plex";
  const [tracks, setTracks] = useState(null);
  const [audio, setAudio] = useState(null);
  const [sub, setSub] = useState(null);
  const [cues, setCues] = useState([]);
  const [busy, setBusy] = useState(false);
  const token = useRef(0);
  const reconvert = useCallback(() => base?.convert && load(base.convert, now()), [base, load, now]);

  const pickSub = useCallback(async (s, remember = true) => {
    const id = ++token.current;
    setSub(s?.id || null);
    setCues([]);
    if (remember) (s?.language ? localStorage.setItem(PREF, s.language) : localStorage.removeItem(PREF));
    try {
      if (plex) {
        await api.post(`/videos/${video.id}/tracks`, { subtitle: s?.id || "" });
        return reconvert();
      }
      const hadBurn = extra.current.burn != null;
      extra.current.burn = s?.kind === "image" ? s.index : null;
      if (s?.kind === "image" || hadBurn) reconvert();
      if (!s || s.kind === "image") return;
      setBusy(true);
      const text = await fetchVtt(video.id, s.id, () => token.current === id);
      if (token.current === id && text) setCues(parseVtt(text));
    } catch (e) {
      if (token.current === id) { toast.error(formatApiError(e)); setSub(null); }
    } finally {
      if (token.current === id) setBusy(false);
    }
  }, [plex, video.id, extra, reconvert]);

  const pickAudio = useCallback(async (a) => {
    setAudio(a.id);
    try {
      if (plex) await api.post(`/videos/${video.id}/tracks`, { audio: a.id });
      else extra.current.audio = a.index;
      reconvert();
    } catch (e) { toast.error(formatApiError(e)); }
  }, [plex, video.id, extra, reconvert]);

  const resetForDirect = useCallback(() => {
    extra.current = {};
    if (tracks?.audio?.length) setAudio(tracks.audio[0].id);
    const cur = tracks?.subtitles?.find((s) => s.id === sub);
    if (plex || cur?.kind === "image") { setSub(null); setCues([]); }
  }, [extra, tracks, sub, plex]);

  useEffect(() => {
    let alive = true;
    api.get(`/videos/${video.id}/tracks`).then(({ data }) => {
      if (!alive) return;
      setTracks(data);
      setAudio(data.selected_audio);
      setSub(data.selected_subtitle);
      const pref = localStorage.getItem(PREF);
      const match = !plex && pref && data.subtitles.filter((s) => s.kind === "text" && s.language === pref && !s.forced)
        .sort((a, b) => (a.source === "external" ? -1 : 1) - (b.source === "external" ? -1 : 1))[0];
      if (match) pickSub(match, false);
    }).catch(() => alive && setTracks({ audio: [], subtitles: [] }));
    return () => { alive = false; token.current++; };
  }, [video.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return { tracks, audio, sub, cues, busy, pickSub, pickAudio, resetForDirect };
}
